import { useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Clapperboard, SearchCheck, Workflow, Globe, MessagesSquare, Plug, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Chapter, Reveal } from "../components/Reveal";
import { WHATSAPP_URL } from "../lib/site";
import { useLang } from "../lib/i18n";

const ICONS = [Clapperboard, SearchCheck, Workflow, Globe, MessagesSquare, Plug];

const CARD_LINKS = [
  "/diensten/reclamevideos",
  "/diensten/website-analyses",
  "/diensten/systemen-bouwen",
  "/diensten/conversiegerichte-websites",
  "/diensten/leadopvolging",
  "/diensten/b2b-integraties",
];

export const Services = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const scrollBy = (dir) => trackRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });

  return (
  <section id="diensten" data-testid="services-section" className="relative overflow-hidden bg-[#181510] py-16 md:py-20">
    <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-amber/10 blur-[140px]" />
    <div className="relative mx-auto max-w-7xl px-6 md:px-12 lg:px-20">
      <Chapter number="02" label={t.chServices} testid="services-chapter" tone="dark" />
      <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
        <Reveal delay={0.08}>
          <h2 className="max-w-2xl font-archivo text-3xl font-black tracking-tight text-white md:text-5xl" data-testid="services-heading">
            {t.servicesHeading}
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:text-amber"
            data-testid="services-whatsapp-link"
          >
            {t.servicesLink}
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        </Reveal>
      </div>
      <Reveal delay={0.14}>
        <p className="mt-5 max-w-3xl text-sm leading-relaxed text-white/70 sm:text-base" data-testid="services-intro">
          {t.servicesIntro}
        </p>
      </Reveal>

      <Reveal delay={0.2}>
        <div
          ref={trackRef}
          className="mt-12 flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="services-carousel"
        >
          {t.services.map((s, i) => {
            const Icon = ICONS[i % ICONS.length];
            const n = `0${i + 1}`;
            return (
              <motion.div
                key={n}
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                className="group relative w-[300px] shrink-0 cursor-pointer snap-start overflow-hidden rounded-[12px] border border-black/10 border-l-4 border-l-amber bg-card p-7 text-left shadow-xl shadow-black/30 transition-colors duration-500 hover:border-amber/60 md:w-[360px] md:p-8"
                id={`dienst-${i}`}
                onClick={() => navigate(CARD_LINKS[i])}
                role="link"
                aria-label={s.title}
                data-testid={`service-card-${i}`}
              >
                <span className="pointer-events-none absolute -right-4 -top-7 font-archivo text-[92px] font-black leading-none text-[#181510]/[0.05] transition-colors duration-500 group-hover:text-[#181510]/[0.1]">
                  {n}
                </span>
                <span className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-edge/70 bg-[#181510] text-amber">
                  <Icon size={20} strokeWidth={2.2} />
                </span>
                <h3 className="mt-6 font-archivo text-xl font-extrabold tracking-tight text-cream">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-tan">{s.copy}</p>
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-faint">4thscenery / {n}</p>
                <p className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 transition-colors duration-300 group-hover:text-amber" data-testid={`service-card-link-${i}`}>
                  {t.viewService}
                  <ArrowUpRight size={12} />
                </p>
              </motion.div>
            );
          })}
        </div>
      </Reveal>

      <Reveal delay={0.26}>
        <div className="mt-4 flex justify-end gap-3">
          <button
            onClick={() => scrollBy(-1)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-white/70 transition-colors duration-300 hover:border-white/60 hover:text-white"
            data-testid="services-prev-button"
            aria-label="Vorige diensten"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => scrollBy(1)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-amber/60 bg-amber/10 text-amber transition-colors duration-300 hover:bg-amber hover:text-[#181510]"
            data-testid="services-next-button"
            aria-label="Meer diensten"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
